import React from 'react'
import { Badge, IconButton, TableCell, TableRow } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { ChatBubbleOutline } from '@material-ui/icons'
import {
    Company,
    EDIT_VENDOR_SUBSCRIPTION_STATUS,
    IdBoolMap,
    PlanStatus,
} from '../../models'
import { PropertyRowType } from './NatPropListTable'
import { axiosInstance, formatPhoneForDisplay } from '../../helpers'
import { useMessageDrawer, useUser } from '../../hooks'
import { ApartmentVendorChannel } from './NationalPropertyList'
import { hasPermission } from '../../models/Users/services'

interface Props {
    row: PropertyRowType
    vendorPlan: string
    setSelectedProperty: (property: Company) => void
    setMessageDrawerOpen: (open: boolean) => void
    apartmentVendorChannels: ApartmentVendorChannel[]
    setApartmentVendorChannels: (
        aptVenChannel: ApartmentVendorChannel[],
    ) => void
    unlockedPropertyMap: IdBoolMap
    setOpenBuyProperty: (open: boolean) => void
    setOpenPropertyDetail: (open: boolean) => void
}

const useStyles = makeStyles((theme: Theme) => ({
    row: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#f8fafc',
        },
    },
    nameCell: {
        fontWeight: 500,
        fontSize: '14px',
    },
    cell: {
        fontSize: '14px',
        borderBottom: `1px solid #e2e8f0`,
    },
}))

export const PropertyRow = (props: Props) => {
    const {
        row,
        setSelectedProperty,
        vendorPlan,
        setMessageDrawerOpen,
        apartmentVendorChannels,
        setApartmentVendorChannels,
        unlockedPropertyMap,
        setOpenPropertyDetail,
    } = props

    const classes = useStyles()
    const { setChannel } = useMessageDrawer()
    const { workspaceUser } = useUser()

    const apartmentVendorChannel = apartmentVendorChannels.find(
        (aptVenChannel) => row.property.id === aptVenChannel.apartment?.id,
    )

    const isUnlocked = unlockedPropertyMap[row.property.id]
    const disabled = !hasPermission(
        workspaceUser,
        EDIT_VENDOR_SUBSCRIPTION_STATUS,
    )

    const handleRowClick = () => {
        if (isUnlocked) {
            setSelectedProperty(row.property)
            setOpenPropertyDetail(true)
        }
    }

    const handleMessageClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        setMessageDrawerOpen(false)
        setSelectedProperty(row.property)
        if (apartmentVendorChannel) {
            const updated = apartmentVendorChannels.map((aptVenChannel) => {
                if (
                    apartmentVendorChannel.channel.id ===
                    aptVenChannel.channel.id
                ) {
                    return {
                        ...aptVenChannel,
                        channel: {
                            ...aptVenChannel.channel,
                            unread_count: 0,
                        },
                    }
                }
                return aptVenChannel
            })
            setApartmentVendorChannels(updated)
            setChannel(apartmentVendorChannel.channel.id)
            setMessageDrawerOpen(true)
        } else {
            axiosInstance
                .post('company/apartment_vendors/get_or_create/', {
                    company_id: row.property.id,
                })
                .then((res) => {
                    setChannel(res.data.channel_id)
                    setMessageDrawerOpen(true)
                })
        }
    }

    const contactLines = [
        formatPhoneForDisplay(row.property.property_manager?.phone),
        row.property.property_manager?.cell_phone !==
        row.property.property_manager?.phone
            ? formatPhoneForDisplay(row.property.property_manager?.cell_phone)
            : null,
        row.property.property_manager?.email,
    ].filter(Boolean)

    return (
        <TableRow
            className={classes.row}
            onClick={handleRowClick}
            style={{ cursor: isUnlocked ? 'pointer' : 'default' }}
        >
            <TableCell className={`${classes.cell} ${classes.nameCell}`}>
                {row.name}
            </TableCell>
            <TableCell className={classes.cell}>{row.address}</TableCell>
            <TableCell className={classes.cell}>
                {contactLines.length > 0 ? contactLines.join(' · ') : '—'}
            </TableCell>
            <TableCell className={classes.cell}>{row.city}</TableCell>
            <TableCell className={classes.cell}>{row.state}</TableCell>
            {vendorPlan === PlanStatus.PREMIUM && (
                <TableCell
                    className={classes.cell}
                    align="center"
                    onClick={(e) => e.stopPropagation()}
                >
                    <IconButton
                        size="small"
                        onClick={handleMessageClick}
                        disabled={disabled}
                    >
                        <Badge
                            badgeContent={
                                apartmentVendorChannel?.channel.unread_count ??
                                0
                            }
                            color="secondary"
                        >
                            <ChatBubbleOutline fontSize="small" />
                        </Badge>
                    </IconButton>
                </TableCell>
            )}
        </TableRow>
    )
}
